import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { CATEGORIES } from "./format";

const EMPTY = { name: "", customer_id: "", description: "", payment_terms_id: "", vat_rate_id: "", billing_route: "" };

export default function AddSourceDialog({ category, customers, onClose, onCreate }) {
  const [form, setForm] = useState(EMPTY);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [vatRates, setVatRates] = useState([]);

  useEffect(() => {
    if (category) {
      setForm(EMPTY);
      Promise.all([api.get("/payment-terms"), api.get("/vat/rates")])
        .then(([t, v]) => {
          setPaymentTerms(t.data.filter((x) => x.active !== false));
          setVatRates(v.data.filter((x) => x.active !== false));
        })
        .catch(() => {});
    }
  }, [category]);

  return (
    <Dialog open={!!category} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="source-dialog">
        <DialogHeader>
          <DialogTitle>New revenue source — {CATEGORIES.find((c) => c.key === category)?.label}</DialogTitle>
          <DialogDescription>Create a manually planned monthly revenue source.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="source-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Customer (optional)</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="source-customer-select"><SelectValue placeholder="No customer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No customer</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Payment terms (cash timing)</Label>
              <Select value={form.payment_terms_id} onValueChange={(v) => setForm({ ...form, payment_terms_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="source-payment-terms-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {paymentTerms.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`source-terms-${t.days}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>VAT rate</Label>
              <Select value={form.vat_rate_id} onValueChange={(v) => setForm({ ...form, vat_rate_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="source-vat-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {vatRates.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`source-vat-${v.rate}`}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-slate-400">
            Payment terms and VAT drive Cash Flow (Build 7) — missing assumptions surface as explicit warnings there, never silent defaults.
          </p>
          {category === "OTHER" && (
            <div className="space-y-1.5">
              <Label>Billing route (Group model)</Label>
              <Select value={form.billing_route || "none"} onValueChange={(v) => setForm({ ...form, billing_route: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="source-billing-route-select"><SelectValue placeholder="Not classified" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not classified</SelectItem>
                  <SelectItem value="VIA_GROUP" data-testid="source-route-via-group">Via Elva Group (customer pass-through)</SelectItem>
                  <SelectItem value="DIRECT" data-testid="source-route-direct">Direct (billed by this company)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">
                Other revenue is never guessed into Group pass-through — an explicit route is required (§12).
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="source-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="source-cancel-button">Cancel</Button>
          <Button onClick={() => onCreate(form)} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="source-save-button">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
